import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import ClientInvoicePrintDocument from '../components/invoicing/ClientInvoicePrintDocument';
import type { ClientInvoiceTemplateInput } from './clientInvoiceTemplate';
import {
  buildClientInvoiceViewModel,
  enrichClientInvoiceViewModelForPrint,
  waitForInvoiceDocumentPaint,
} from './clientInvoiceTemplate';

async function renderInvoiceCaptureNode(
  input: ClientInvoiceTemplateInput,
): Promise<{ node: HTMLDivElement; cleanup: () => void }> {
  const vm = await enrichClientInvoiceViewModelForPrint(buildClientInvoiceViewModel(input));
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  Object.assign(host.style, {
    position: 'fixed',
    left: '-10000px',
    top: '0',
    zIndex: '-1',
    background: '#ffffff',
  });
  document.body.appendChild(host);

  const root = createRoot(host);
  flushSync(() => {
    root.render(createElement(ClientInvoicePrintDocument, { viewModel: vm }));
  });

  const node = host.firstElementChild as HTMLDivElement | null;
  if (!node) {
    root.unmount();
    host.remove();
    throw new Error('Invoice render failed');
  }

  await waitForInvoiceDocumentPaint(node);

  return {
    node,
    cleanup: () => {
      root.unmount();
      host.remove();
    },
  };
}

export async function buildClientInvoicePdfDoc(
  input: ClientInvoiceTemplateInput,
): Promise<jsPDF> {
  const { node, cleanup } = await renderInvoiceCaptureNode(input);
  try {
    const canvas = await html2canvas(node, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: node.scrollWidth,
      windowHeight: node.scrollHeight,
    });

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const imgData = canvas.toDataURL('image/png');

    let heightLeft = imgHeight;
    let position = 0;

    doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      doc.addPage();
      doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    return doc;
  } finally {
    cleanup();
  }
}

export async function generateClientInvoicePdf(
  input: ClientInvoiceTemplateInput,
  filename?: string,
): Promise<void> {
  const doc = await buildClientInvoicePdfDoc(input);
  const invNum = input.invoice.invoiceNumber.replace(/[^\w-]/g, '_');
  doc.save(filename ?? `client-tax-invoice-${invNum}.pdf`);
}

export async function getClientInvoicePdfBlobUrl(
  input: ClientInvoiceTemplateInput,
): Promise<string> {
  const doc = await buildClientInvoicePdfDoc(input);
  return doc.output('bloburl').toString();
}
